import { CalendarPreferences } from '@xeetr/types/models'
import { useCallback } from 'react'

import { useUpdateUserSetting } from './useUpdateUserSetting'

/**
 * Hook for updating calendar preferences.
 * Stores preferences per-user, per-tenant in user.settings.calendar.
 * Only persists settings that should sync cross-device (not temporary UI state).
 * Supports partial updates - backend handles deep merging.
 *
 * @returns Object with updateCalendarPreferences function and isUpdating state
 *
 * @example
 * ```tsx
 * const { updateCalendarPreferences, isUpdating } = useUpdateCalendarPreferences()
 * updateCalendarPreferences({ showDaysWithoutActivities: true })
 * ```
 */
export const useUpdateCalendarPreferences = () => {
  const { update, isUpdating } = useUpdateUserSetting(
    'calendar',
    'Fehler beim Speichern der Kalender-Einstellungen.',
    'Kalender-Einstellungen gespeichert.'
  )

  const updateCalendarPreferences = useCallback(
    (preferences: Partial<CalendarPreferences>) => {
      // Backend handles deep merge, just pass partial update
      update(preferences as CalendarPreferences)
    },
    [update]
  )

  return { updateCalendarPreferences, isUpdating }
}
